import _, { get } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { usePlacesWidget } from "react-google-autocomplete";
import { Control, Controller } from "react-hook-form";
import { Form, Grid, Input, SemanticWIDTHS, Label } from "semantic-ui-react";
import AppSettings from "../../../config";
import { parseGoogleAddress } from "../../../utils/ParseGoogleAddress";
import { makeFullAddress } from "../../../utils/functions";
import { countryPostalConnector, iso3ToIso2, mexicoStates3To2 } from "../../../constants/CountryCodesMap";
import useArcgisWidget from "../../../features/mapping/hooks/useArcGisWidget";
import useCountries from "../../../hooks/useCountries";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { FieldLabelWithButton } from "../FieldLabelWithButton";
import PlaceholderUntilLoaded from "../PlaceholderUntilLoaded";
import { Country, formatAddressFields } from "./formatAddress";
import { AccessibleDropdown } from "./accessible/AccessibleDropdown";
import { useMedia } from "react-media";
import "./ControlledAutofilledAddress.scss";

interface IControlledAutofilledAddress {
  control: Control;
  name: string;
  coordName?: string;
  autofillCountry?: boolean;
  defaultValue?:
    | {
        address?: string;
        address2?: string;
        city?: string;
        state?: string;
        zip?: string;
        countryId?: string;
      }
    | { [key: string]: any };
  disabled?: boolean;
  required?: boolean;
  readonly?: boolean;
  errors?: any;
  validate?: {
    [name: string]: (value: {
      attn: string | null;
      address: string | null;
      address2: string | null;
      city: string | null;
      state: string | null;
      zip: string | null;
      countryId: string | null;
    }) => string | undefined;
  };

  autofill?: boolean;

  showAttnField?: boolean;
  showAttnFieldToggle?: boolean;
  showCountryField?: boolean;

  customAttnLabel?: string;
  customAddressLabel?: string;
  customAddress2Label?: string;
  customCityLabel?: string;
  customStateLabel?: string;
  customZipLabel?: string;

  customAttnSubLabel?: string;
  customAddressSubLabel?: string;
  customAddress2SubLabel?: string;
  customCitySubLabel?: string;
  customStateSubLabel?: string;
  customZipSubLabel?: string;

  customCountryLabel?: string;
  customCountrySubLabel?: string;

  valueAccessor?: string; // Used to access the address object if the control is a nested object (e.g. used in locations custom fields to reach "valueAddress")

  transform?: {
    input: (value: any) => any;
    output: (value: any) => any;
  };

  customLayout?: Array<Array<{ field: string; width: number; class?: string }>>;
  customGridClass?: string;
  labelClassName?: string;
}

export default function ControlledAutofilledAddress({
  control,
  name,
  coordName,
  autofillCountry = true,
  defaultValue,
  required = false,
  disabled = false,
  readonly,
  errors,
  validate,

  autofill = true,

  showAttnField: showAttnFieldProp = false,
  showAttnFieldToggle = false,
  showCountryField = false,

  customAttnLabel,
  customAddressLabel,
  customAddress2Label,
  customCityLabel,
  customStateLabel,
  customZipLabel,

  customAttnSubLabel,
  customAddressSubLabel,
  customAddress2SubLabel,
  customCitySubLabel,
  customStateSubLabel,
  customZipSubLabel,

  valueAccessor,

  transform = {
    input: (value) => {
      // Ensure all fields are always represented
      if (!value)
        return {
          attn: null,
          address: null,
          address2: null,
          city: null,
          state: null,
          zip: null,
          countryId: null,
        };
      return value;
    },
    output: (value) => {
      // Return null if all fields are empty
      const addressEmpty = Object.entries(value).every(([key, value]) => {
        if (key === "id") return true;
        return value === "" || value === null;
      });
      if (addressEmpty) return null;
      return value;
    },
  },
  customLayout,
  customGridClass,
  labelClassName,
}: IControlledAutofilledAddress) {
  const { isEnabled: useArcgis } = useFeatureFlag("global.esri_autocomplete");
  const isSmallScreen = useMedia({ query: "(max-width: 520px)" });
  const { setValue, getValues, trigger } = control;
  const errorsRef = useRef(errors); // We need to keep a reference to the errors object to access it in the Google Places API callback

  const { countries, countryDropdownOptions, agencyCountry, countriesLoading, agencyCountryLoading } = useCountries({
    // This function sets the default value of the country flag once the agency country is loaded
    onLoaded: () => {
      const fieldValues = getValues(name);
      if (!autofillCountry || !showCountryField || !agencyCountry) return;
      const currentValues = fieldValues ?? defaultValue ?? {}; // Retrieve current (or default) values

      if (!!valueAccessor && valueAccessor in currentValues) {
        if (currentValues?.[valueAccessor]?.countryId) return; // If countryId is already set, do nothing
        const currentValueAddress = currentValues[valueAccessor];
        setValue(name, { ...currentValues, valueAddress: { ...currentValueAddress, countryId: agencyCountry.id } });
      } else {
        if (currentValues.countryId) return; // If countryId is already set, do nothing
        setValue(name, { ...currentValues, countryId: agencyCountry.id });
      }
    },
  });

  const countryOptionsWithLabel = countryDropdownOptions.map(({ key, value, text }) => ({ key, value, label: text }));

  const [showAttnField, setShowAttnField] = useState<boolean>(
    !!transform.input(defaultValue)?.attn || showAttnFieldProp,
  );

  // state to track if google places api is ready; we want to update this when the script loads to force a remount
  const [googleReady, setGoogleReady] = useState(!!window.google?.maps?.places);
  useEffect(() => {
    if (googleReady) return;

    const checkInterval = setInterval(() => {
      if (window.google?.maps?.places) {
        setGoogleReady(true);
        clearInterval(checkInterval);
      }
    }, 100);

    setTimeout(() => clearInterval(checkInterval), 10000);

    return () => clearInterval(checkInterval);
  }, [googleReady]);

  /**
   * Keep errors reference up to date
   */
  useEffect(() => {
    errorsRef.current = errors;
  }, [errors]);

  /**
   * Load Google Places API script if not already loaded for staff
   */
  useEffect(() => {
    if (window.google?.maps?.places) return;
    if (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) return;

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${AppSettings.GOOGLE_GEOCODING_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, []);

  /**
   * Retrieves the country data for the current countryId. If no countryId is provided, the agency country is used. Otherwise the default is "US".
   *
   * @param countryId - The ISO2 country to retrieve data for
   */
  const getCountryData = (countryId?: string): Country => {
    const country = countryId || agencyCountry?.id || "US";
    return countries[country];
  };

  const anyFieldsFilledIn = (value: any) => {
    const transformedValue = transform.input(value);
    if (!transformedValue) return false;
    const { address, city, state, zip } = transformedValue;
    return address || city || state || zip;
  };

  const allFieldsFilledIn = (value: any, countryUsesState: boolean = true) => {
    const transformedValue = transform.input(value);
    if (!transformedValue) return false;
    const { address, city, state, zip } = transformedValue;
    return address && city && (state || !countryUsesState) && zip;
  };

  // Country data
  const controlVals = control.watchInternal();
  const controlAccessor = valueAccessor ? `${name}.${valueAccessor}` : name;
  const addressObj = _.get(controlVals, controlAccessor); // The address object from the control
  const countryId: string = addressObj?.countryId ?? "US";

  const countryData: Country = getCountryData(countryId);
  const stateOptions =
    countryData?.states?.map((state: string) => ({
      key: state,
      text: state,
      value: state,
      label: state,
      disabled: false,
    })) ?? null;

  /* 
    Add the default state to the options list if it's not already there (this is to handle dirty data in the database,
    as we don't want the field to appear blank. But the user will still have to select a valid state to submit the form)
  */
  const defaultState: string = addressObj?.state ?? "";
  if (defaultState && stateOptions && !stateOptions.find((option) => option.key === defaultState)) {
    stateOptions.push({
      key: defaultState,
      text: defaultState,
      value: defaultState,
      label: defaultState,
      disabled: true,
    });
  }

  // If address is set to read-only, display address as a string
  if (readonly) {
    const formattedAddress = makeFullAddress(
      defaultValue?.address,
      defaultValue?.address2,
      defaultValue?.city,
      defaultValue?.state,
      defaultValue?.zip,
      null,
    );
    return (
      <Grid.Row>
        <Grid.Column>
          <Form.Field>
            <label htmlFor='address'>{customAddressLabel || "Address"}</label>
            {formattedAddress ? (
              <span id='address'>{formattedAddress}</span>
            ) : (
              <span id='address' style={{ color: "gray" }}>
                N/A
              </span>
            )}
          </Form.Field>
        </Grid.Column>
      </Grid.Row>
    );
  }

  // Otherwise render normally
  return (
    <Controller
      key={`autocomplete-input-${useArcgis ? "arcgis" : googleReady ? "google-ready" : "google-loading"}`}
      control={control}
      name={name}
      defaultValue={defaultValue}
      rules={{
        validate: {
          addressRequired: (value) => {
            if (!required) return;
            if (!transform.input(value)?.address) return "Address is required";
          },
          countryRequired: (value) => {
            if (!required) return;
            if (!transform.input(value)?.countryId) return "Country is required";
          },
          cityRequired: (value) => {
            if (!required) return;
            if (!transform.input(value)?.city) return "City is required";
          },
          stateRequired: (value) => {
            const addressObj = valueAccessor ? value[valueAccessor] : value; // Need to use valueAccessor to support nested objects
            const countryData = getCountryData(addressObj?.countryId);
            if (!required || !countryData.hasStateInAddress) return;
            if (!transform.input(value)?.state) return "State is required";
          },
          zipRequired: (value) => {
            if (!required) return;
            if (!transform.input(value)?.zip) return "Zip is required";
          },
          allFieldsIfAny: (value) => {
            const addressObj = valueAccessor ? value[valueAccessor] : value; // Need to use valueAccessor to support nested objects
            const countryData = getCountryData(addressObj?.countryId);
            if (allFieldsFilledIn(value, countryData.hasStateInAddress)) return;
            if (anyFieldsFilledIn(value)) {
              if (!countryData.hasStateInAddress) {
                return "Address, City, and Zip must have a value";
              } else {
                return "Address, City, State, and Zip must have a value";
              }
            }
          },
          // Validates that zip code matches regex for the selected country
          zipMatchesFormat: (value) => {
            if (!required && !anyFieldsFilledIn(value)) return;
            const addressObj = valueAccessor ? value[valueAccessor] : value;
            const countryData = getCountryData(addressObj?.countryId);

            const postalFormat = countryData?.postalFormat;
            if (postalFormat) {
              const regex = new RegExp(postalFormat, "g");
              if (!regex.test(addressObj?.zip)) return "Zip must match the postal format";
            }
          },
          // If the country has states and a state is provided, validate that the state is in the list of states
          validState: (value) => {
            const addressObj = valueAccessor ? value[valueAccessor] : value;
            const countryData = getCountryData(addressObj?.countryId);

            if (addressObj?.state && countryData?.states && !countryData.states.includes(addressObj.state)) {
              return `Invalid state value: '${addressObj.state}'`;
            }
          },
          custom: (value) => {
            const validationFunctions = _.values(validate);
            for (const validationFunction of validationFunctions) {
              const response = validationFunction(transform.input(value));
              if (response) return response;
            }
          },
        },
      }}
      render={({ onChange, value }) => {
        const { ref } = (() => {
          const arcgisRef = useArcgisWidget({
            onPlaceSelected: (place) => {
              if (!place.attributes.StAddr) return;
              const { StAddr, ShortLabel, City: city, RegionAbbr, Postal, PostalExt, Country } = place.attributes;
              const address = StAddr || ShortLabel || "";
              const countryId = iso3ToIso2[Country]?.toLocaleUpperCase() || "US";
              const zip = Postal + (PostalExt ? `${countryPostalConnector[countryId]}${PostalExt}` : "");
              // Validator uses the 2 letter mexico state codes, but esri returns 3 letter codes
              const state = countryId === "MX" ? mexicoStates3To2[RegionAbbr] || RegionAbbr : RegionAbbr;
              const coords = place.location;
              if (coordName && coords) {
                setValue(coordName, `${coords.x},${coords.y}`);
                trigger(coordName);
              }

              handleChange({
                address,
                city,
                state,
                zip,
                countryId,
              });

              // Bit of a hack to get it to revalidate after inserting new values
              if (Object.keys(errorsRef.current).length > 0) trigger(name);
            },
          });

          const googleRef = usePlacesWidget({
            apiKey: AppSettings.GOOGLE_GEOCODING_KEY,
            onPlaceSelected: (place) => {
              if (!place.address_components) return;
              const addressObj = parseGoogleAddress(place.address_components);
              const address = addressObj.full_street || place.name || "";
              const city = addressObj.city;
              const zip = addressObj.postal_code;
              const countryId = addressObj.country;
              const state = addressObj.region_short.length <= 3 ? addressObj.region_short : ""; // If state is provided non-abbreviated, don't use it

              handleChange({
                address,
                city,
                state,
                zip,
                countryId,
              });

              // Bit of a hack to get it to revalidate after inserting new values
              if (Object.keys(errorsRef.current).length > 0) {
                trigger(name);
              }
            },
            options: {
              types: [],
              fields: ["address_components", "name"],
            },
          });
          return useArcgis ? arcgisRef : googleRef;
        })();

        const handleChange = (change: { [key: string]: string }) => {
          onChange(
            transform.output({
              ...transform.input(value),
              ...change,
            }),
          );
        };

        const cityStateZipHasSubLabel = customCitySubLabel || customStateSubLabel || customZipSubLabel;
        const subLabelColor = disabled ? "rgba(0, 0, 0, 0.3)" : undefined;
        const stateLabel = customStateLabel || countryId === "US" ? "State" : "State/Province";

        const attn = (
          <Form.Field className='tw-w-auto' disabled={disabled} error={!!errors?.[name]}>
            <label htmlFor='attn' className={labelClassName || ""}>
              {customAttnLabel || "Attn"}
            </label>
            {customAttnSubLabel && (
              <div className='sub-label' style={{ color: subLabelColor }}>
                {customAttnSubLabel}
              </div>
            )}
            <Input
              type='text'
              disabled={disabled}
              value={transform.input(value)?.attn ?? ""}
              onChange={(_, { value }) => handleChange({ attn: value })}
            />
          </Form.Field>
        );

        const address = (
          <Form.Field className='tw-w-auto' disabled={disabled} error={!!errors?.[name]}>
            <FieldLabelWithButton
              buttonText='Add Attn'
              label={customAddressLabel || "Address"}
              htmlFor='address'
              required={required}
              showButton={!showAttnField && showAttnFieldToggle && !value?.attn}
              onClick={(e) => {
                e.preventDefault();
                setShowAttnField(true);
              }}
              className={labelClassName || ""}
            />
            {customAddressSubLabel && (
              <div className='sub-label' style={{ color: subLabelColor }}>
                {customAddressSubLabel}
              </div>
            )}
            <div className='ui input'>
              <input
                id='address'
                type='text'
                ref={autofill ? (ref as any) : undefined}
                disabled={disabled}
                value={transform.input(value)?.address ?? ""}
                autoComplete='address-line1'
                onChange={(e) => handleChange({ address: e.target.value })}
                placeholder=''
              />
            </div>
          </Form.Field>
        );

        const country = (
          <Form.Field className='tw-w-auto' required={required} disabled={disabled} error={!!errors?.[name]}>
            <label htmlFor='country'>Country</label>

            <AccessibleDropdown
              classNamePrefix='accessible-input'
              id='country'
              clearable={true}
              options={countryOptionsWithLabel}
              value={countryOptionsWithLabel.find((option) => option.value === countryId) ?? null}
              onChange={(newValue) =>
                handleChange({
                  countryId: (newValue && "value" in newValue ? newValue.value : "") ?? "",
                })
              }
              disabled={disabled}
              placeholder=''
              error={errors?.[name]}
              showErrorLabel={false}
            />
          </Form.Field>
        );

        const address2 = (
          <Form.Field className='tw-w-auto' disabled={disabled} error={!!errors?.[name]}>
            <label htmlFor='address2' className={labelClassName || ""}>
              {customAddress2Label || "Apt, Suite, Floor, Other"}
            </label>
            {customAddress2SubLabel && (
              <div className='sub-label' style={{ color: subLabelColor }}>
                {customAddress2SubLabel}
              </div>
            )}

            <Input
              id='address2'
              type='text'
              disabled={disabled}
              value={transform.input(value)?.address2 ?? ""}
              onChange={(_, { value }) => handleChange({ address2: value })}
              autoComplete='address-line2'
            />
          </Form.Field>
        );

        const city = (
          <Form.Field
            className='tw-w-auto tw-text-nowrap'
            required={required}
            disabled={disabled}
            error={!!errors?.[name]}
          >
            <label htmlFor='city' className={labelClassName || ""}>
              {customCityLabel || "City"}
            </label>
            {cityStateZipHasSubLabel && (
              <div className='sub-label' style={{ color: subLabelColor }}>
                {customCitySubLabel}
              </div>
            )}
            <Input
              id='city'
              type='text'
              value={transform.input(value)?.city ?? ""}
              onChange={(_, { value }) => handleChange({ city: value })}
              disabled={disabled}
              autoComplete='address-level2'
            />
          </Form.Field>
        );

        const state = (
          <Form.Field
            className='tw-w-auto tw-text-nowrap'
            required={required}
            disabled={disabled}
            error={!!errors?.[name]}
          >
            <label htmlFor='state' className={labelClassName || ""}>
              {stateLabel}
            </label>
            {cityStateZipHasSubLabel && (
              <div className='sub-label' style={{ color: subLabelColor }}>
                {customStateSubLabel}
              </div>
            )}
            {/* Provide dropdown options if they exist in the database, otherwise make it a free text field */}
            {stateOptions ? (
              <AccessibleDropdown
                classNamePrefix='address-state-dropdown'
                id='state'
                clearable={true}
                options={stateOptions}
                value={stateOptions.find((option) => option.value === value?.state) ?? null}
                onChange={(newValue: any) => handleChange({ state: newValue?.value ?? "" })}
                disabled={disabled}
                placeholder=''
                error={errors?.[name]}
                showErrorLabel={false}
              />
            ) : (
              <Input
                id='state'
                type='text'
                value={transform.input(value)?.state ?? ""}
                onChange={(_, { value }) =>
                  handleChange({
                    state: value
                      .toUpperCase()
                      .trim()
                      .slice(0, 3)
                      .replace(/[^A-Z]/g, ""),
                  })
                }
                disabled={disabled}
                autoComplete='address-level1'
              />
            )}
          </Form.Field>
        );

        const zip = (
          <Form.Field
            className='tw-w-auto tw-text-nowrap'
            required={required}
            disabled={disabled}
            error={!!errors?.[name]}
          >
            <label htmlFor='zip' className={labelClassName || ""}>
              {customZipLabel || "Zip"}
            </label>
            {cityStateZipHasSubLabel && (
              <div className='sub-label' style={{ color: subLabelColor }}>
                {customZipSubLabel}
              </div>
            )}
            <Input
              id='zip'
              type='text'
              value={transform.input(value)?.zip ?? ""}
              onChange={(_, { value }) => handleChange({ zip: value })}
              disabled={disabled}
              autoComplete='postal-code'
            />
          </Form.Field>
        );

        // Map the address fields to their respective JSX elements
        const addressJsx: {
          [key: string]: JSX.Element;
        } = {
          attn,
          address,
          address2,
          country,
          city,
          state,
          zip,
        };

        // Format the address fields based on the country
        const addressFormat:
          | {
              field: string;
              width: number;
              class?: string | undefined;
            }[][]
          | undefined =
          customLayout ||
          formatAddressFields({
            country: countryData ?? agencyCountry,
            showAttn: showAttnField || value?.attn,
            mobileView: isSmallScreen,
          });

        return (
          <PlaceholderUntilLoaded isLoading={countriesLoading || agencyCountryLoading} lineCount={5}>
            {/* Invisible controller for the coords */}
            {coordName && (
              <Controller
                name={coordName}
                control={control}
                defaultValue={null}
                render={(field) => <input {...field} type='hidden' />}
              />
            )}

            {/* Address fields */}
            <Grid className={`controlled-autofilled-address ${customGridClass}`}>
              {addressFormat.map((columns, i) => {
                return (
                  <Grid.Row key={i}>
                    {columns.map((col, i) => {
                      return (
                        <Grid.Column key={i} width={col.width as SemanticWIDTHS} className={col.class ?? ""}>
                          {addressJsx[col.field]}
                        </Grid.Column>
                      );
                    })}
                  </Grid.Row>
                );
              })}
              {!!get(errors, name) && (
                <Grid.Row>
                  <Grid.Column>
                    <Label basic prompt pointing='above' aria-live='polite' color='red'>
                      {get(errors, name + ".message")}
                    </Label>
                  </Grid.Column>
                </Grid.Row>
              )}
            </Grid>
          </PlaceholderUntilLoaded>
        );
      }}
    />
  );
}
